import { FC } from 'react';
import {
  Box,
  FormHelperText,
  SxProps,
  TextFieldProps,
  Theme,
  Typography,
} from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { Controller, FieldError } from 'react-hook-form';
import { PermissionScope } from 'types/apps';
import { useAppAccessContext } from 'contexts/AppAccessContext';
import {
  CustomValidationType,
  HookFormComponentProps,
} from 'ui-component/HookFormComponents/types';
import _ from 'lodash';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const getValidationColor = (theme: Theme, type?: string | undefined) => {
  switch (type) {
    case CustomValidationType.WARNING:
      return theme.palette.warning.main;
    default:
      return theme.palette.error.main;
  }
};

const StyledFormHelperText = styled(FormHelperText, {
  shouldForwardProp: (prop) => prop !== 'errorType',
})<{ errorType?: string | undefined }>(({ theme, errorType }) => ({
  '&.Mui-error': {
    color: getValidationColor(theme, errorType),
  },
}));

export type HookFormQuillInputProps = {
  permissionScope?: PermissionScope;
  boxSx?: SxProps<Theme>;
  helperText?: string;
  hideLabel?: boolean;
} & TextFieldProps &
  HookFormComponentProps;

export const HookFormQuillInput: FC<HookFormQuillInputProps> = ({
  errors,
  name = '',
  control,
  boxSx,
  permissionScope,
  helperText,
  label = '',
  hideLabel = false,
  disabled = false,
  autoFocus = false,
}) => {
  const theme = useTheme();
  const { isPermissionDisabled } = useAppAccessContext();

  const permissionDisabled = isPermissionDisabled(permissionScope);

  const errorType = _.get(errors, name)?.type as string | undefined;

  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { onChange, value, onBlur } }) => (
        <Box
          sx={{
            mb: 2,
            ...boxSx,
            '& .quill': {
              bgcolor: theme.palette.mode === 'dark' ? 'dark.main' : 'grey.50',
              borderRadius: '12px',
              '& .ql-toolbar': {
                bgcolor:
                  theme.palette.mode === 'dark' ? 'dark.light' : 'grey.100',
                borderColor:
                  theme.palette.mode === 'dark'
                    ? theme.palette.dark.light + 20
                    : 'grey.400',
                borderTopLeftRadius: '12px',
                borderTopRightRadius: '12px',
              },
              '& .ql-container': {
                borderColor:
                  theme.palette.mode === 'dark'
                    ? `${theme.palette.dark.light + 20} !important`
                    : `${theme.palette.grey[400]} !important`,
                borderBottomLeftRadius: '12px',
                borderBottomRightRadius: '12px',
                '& .ql-editor': {
                  minHeight: 125,
                },
              },
            },
          }}
        >
          {!hideLabel && (
            <Typography variant="h6" color="text.secondary">
              {label}
            </Typography>
          )}
          <ReactQuill
            theme="snow"
            value={permissionDisabled ? '' : value ?? ''}
            onBlur={onBlur}
            onChange={onChange}
            readOnly={disabled}
          />
          {helperText && (
            <StyledFormHelperText
              id={`${name}HelperText`}
              errorType={errorType}
            >
              {helperText}
            </StyledFormHelperText>
          )}
          {_.get(errors, name)?.message && (
            <StyledFormHelperText
              error
              id={`${name}Error`}
              errorType={errorType}
            >
              {(_.get(errors, name) as FieldError)?.message}
            </StyledFormHelperText>
          )}
        </Box>
      )}
    />
  );
};
